<template>
    <LoadingPanel
        v-model:collapsed="collapsed"
        :headline="t('common.componentCommentsHeadline')"
        :is-pending="isPending"
        :is-error="isError"
        collapsible
    >
        <ComponentComments
            :data="data"
            :general-comment-component-sid="props.componentId"
            @delete-comment="deleteComment"
            @edit-comment="editComment"
        />
        <template #context>
            <pbl-button
                v-if="canEditComments"
                slot="context"
                variant="secondary"
                compact
                @click.stop="commentModal?.openComment()"
            >
                <pbl-icon
                    slot="icon"
                    name="plus"
                ></pbl-icon>
                {{ t('common.addComment') }}
            </pbl-button>
        </template>
    </LoadingPanel>
</template>

<script setup lang="ts">
import LoadingPanel from '@/components/panel/LoadingPanel.vue'
import { useI18n } from 'vue-i18n'
import { usePermissions } from '@/composables/usePermissions'
import ComponentComments from '@/components/comments/ComponentComments.vue'
import { DeleteCommentPayload } from '@/types/comments'
import { useTransformerGeneralCommentsQuery } from '@/api/queries/transformers/general-comments/useTransformerGeneralCommentsQuery'
import { ComponentCommentOpenPayload, useComponentCommentModal } from '@/components/comments/component-comment/useComponentCommentModal'
import { ComponentType } from '@/configuration/component/component-types'
import { useGeneralCommentsDeleteMutation } from '@/api/mutations/general-comments/useGeneralCommentsDeleteMutation'
import { useGeneratorGeneralCommentsQuery } from '@/api/queries/generators/general-comments/useGeneratorGeneralCommentsQuery'
import { computed } from 'vue'

const { t } = useI18n()
const commentModal = useComponentCommentModal()

const collapsed = defineModel<boolean>('collapsed', { default: true })

const props = defineProps<{
    componentId: string,
    componentType: ComponentType,
}>()

const { userPermissions } = usePermissions()
const canEditComments = computed(() => {
    if (props.componentType === ComponentType.Transformer) {
        return userPermissions.value.canEditTransformerComments
    } else if (props.componentType === ComponentType.Generator) {
        return userPermissions.value.canEditGeneratorComments
    }
    return false
})

const { data: transformerData, isPending: isTransformerPending, isError: isTransformerError } = useTransformerGeneralCommentsQuery(
    props.componentType === ComponentType.Transformer ? props.componentId : undefined
)
const { data: generatorData, isPending: isGeneratorPending, isError: isGeneratorError } = useGeneratorGeneralCommentsQuery(
    props.componentType === ComponentType.Generator ? props.componentId : undefined
)

const data = computed(() => props.componentType === ComponentType.Transformer ? transformerData.value : generatorData.value)
const isPending = computed(() => props.componentType === ComponentType.Transformer ? isTransformerPending.value : isGeneratorPending.value)
const isError = computed(() => props.componentType === ComponentType.Transformer ? isTransformerError.value : isGeneratorError.value)

const { mutateAsync: mutateDeleteAsync } = useGeneralCommentsDeleteMutation(props.componentType)

const editComment = (commentDto: ComponentCommentOpenPayload) => {
    commentModal?.openComment(commentDto)
}

const deleteComment = async (payload: DeleteCommentPayload) => {
    await mutateDeleteAsync({
        generalCommentComponentSid: props.componentId,
        commentId: payload.commentId,
    })
}
</script>